import { useEffect } from "react";
import { CanvasPanel } from "../canvas-panel/canvas-panel";
import { ResizableChatPanel } from "../chat-panel/resizable-chat-panel";
import { imagineStore } from "@/stores/imagine.store";
import { imagineRoutingStore } from "@/wrapper/imagine-routing.store";
import { AnimatePresence, motion } from "motion/react";
import { useImagineChat } from "@/providers/imagine-chat-provider";
import { analytics, posthogClient, trackAnalyticsEvent } from "@/lib/analytics";
import { useMessageHistory } from "@/providers/message-history-provider";

export type StudioViewProps = {
  projectId: string;
  region: string;
  readonly: boolean;
  isGeneratingNewProject: boolean;
};

export function StudioView(props: StudioViewProps) {
  const setIsReadonly = imagineStore.use.setIsReadonly();
  const setProjectId = imagineStore.use.setProjectId();
  const chatPanelCollapsed = imagineStore.use.ui.chatPanelCollapsed();
  const showStudioView = imagineRoutingStore.use.showStudioView();
  const project = imagineStore.use.project();
  const { resumeStream } = useImagineChat();
  const setHistory = imagineStore.use.chat.setHistory();
  const chat = useImagineChat();
  const { projectId, isGeneratingNewProject, readonly } = props;
  const navigate = imagineRoutingStore.use.navigate();

  useEffect(() => {
    if (project?.id !== projectId) {
      setProjectId(projectId);
    }
  }, [projectId, setProjectId, project?.id]);

  useEffect(() => {
    setIsReadonly(readonly);
  }, [readonly, setIsReadonly]);

  const {
    messages: fetchedHistory,
    isFinished: isFinishedFetchingProjectMessages,
  } = useMessageHistory();

  useEffect(() => {
    // Set project context for analytics and emit view event
    if (projectId) {
      analytics.setGlobalProperties({ projectId });
      posthogClient.register({ projectId });
      const isOwn = !props.readonly;
      trackAnalyticsEvent(
        isOwn ? "project.viewed_own" : "project.viewed_public",
        {
          projectId,
        },
      ).catch(() => {});
    }
  }, [projectId, props.readonly]);

  // Whenever the studio renders and we are not in the progress of generating a new project,
  // we want to reset the chat state. We should keep the chat state for ongoing generations
  // because if errors happen between submission and generation, we should be able to see them.
  useEffect(() => {
    if (!isGeneratingNewProject) {
      chat.reset();
    }
  }, [isGeneratingNewProject]);

  useEffect(() => {
    if (isFinishedFetchingProjectMessages && fetchedHistory) {
      console.log("fetchedHistory", fetchedHistory);
      setHistory(fetchedHistory);
    }
  }, [fetchedHistory, setHistory, isFinishedFetchingProjectMessages]);

  // This is not an in-progress generation for the first time, we expect a clean salte
  // and want to make sure message history is fetched before resuming the stream, to ensure
  // messages appear in order.
  useEffect(() => {
    if (project?.hasActiveStream) {
      if (!isGeneratingNewProject && isFinishedFetchingProjectMessages) {
        resumeStream({ projectId: projectId });
        return;
      }
    }
  }, [project, isFinishedFetchingProjectMessages, resumeStream]);

  // This is a continuatino of a new project from the prompt page, we do not want to fetch
  // the message history because it might include duplicate.
  // We also do not want to clear the chat state so we don't lose errors or other state.
  useEffect(() => {
    if (isGeneratingNewProject) {
      resumeStream({ projectId: projectId });
      return;
    }
  }, [isGeneratingNewProject, projectId, resumeStream]);

  return (
    <AnimatePresence mode="wait" key="studio-view">
      {showStudioView && (
        <motion.div
          key="studio-view"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 1, ease: "easeInOut" }}
          className="flex w-full h-full min-w-0"
        >
          <ResizableChatPanel collapsed={chatPanelCollapsed} />
          <div className="flex-1 min-w-0">
            <CanvasPanel
              isGeneratingNewProject={isGeneratingNewProject}
              projectId={projectId}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
